import { c as _c } from "react/compiler-runtime";
import { styled, Theme } from '@classify/react-core-components/styles';
import Typography from '@classify/react-core-components/Typography';
import Tag from '@classify/react-core-components/Tag';
import { translate } from '@openclassrooms/translations';
import Button from '@classify/react-core-components/Button';
import Link from 'next/link';
import Line from './Line';
import type { OfferData, OfferTopic } from '../../types';
interface Props {
  topics: OfferTopic[];
  coursesData: OfferData;
  diplomaData: OfferData;
}
const Table = styled('table')(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  borderCollapse: 'collapse',
  width: '100%',
  '& tr': {
    display: 'grid',
    gridTemplateColumns: '0 1fr 1fr',
    [breakpoints.up('sm')]: {
      gridTemplateColumns: 'minmax(160px, 194px) 1fr 1fr'
    }
  },
  '& td, & th': {
    padding: spacing(2, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  '& th': {
    padding: 0
  },
  '& td': {
    textAlign: 'center'
  }
}));
const Thead = styled('thead')(({
  theme: {
    palette,
    spacing
  }
}) => ({
  borderBottom: `1px solid ${palette.layout.line.divider}`,
  th: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 0,
    '&:nth-of-type(2)': {
      backgroundColor: palette.notification.neutral.background
    },
    '&:nth-of-type(3)': {
      backgroundColor: palette.content.highlight.secondLevel.background
    }
  },
  '& tr:last-child th': {
    paddingBottom: spacing(2)
  }
}));
export const IconTextWrapper = styled('div', {
  shouldForwardProp: prop => !['iconShouldBeSmall'].includes(prop as string)
})<{
  theme?: Theme;
  iconShouldBeSmall?: boolean;
}>(({
  theme: {
    spacing,
    breakpoints
  }
}) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  height: '100%',
  gap: spacing(1),
  '& svg': {
    fontSize: '16px',
    [breakpoints.up('md')]: {
      fontSize: '20px'
    }
  },
  [breakpoints.up('sm')]: {
    flexDirection: 'row',
    height: 'auto',
    '& :last-child': {
      flexGrow: 0
    }
  }
}));
export default function ComparativeTable(t0) {
  const $ = _c(59);
  const {
    topics,
    coursesData,
    diplomaData
  } = t0;
  const CourseIcon = coursesData.icon;
  const DiplomaIcon = diplomaData.icon;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <th />;
    t2 = <th />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <tr>{t1}{t2}<th><Tag color="info" size="small" sx={{
          mt: 2,
          mb: 1
        }}>{translate("global.word.recommended")}</Tag></th></tr>;
    t4 = <th />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== CourseIcon) {
    t5 = <CourseIcon />;
    $[4] = CourseIcon;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== coursesData.title) {
    t6 = <Typography variant="h6" component="h2">{coursesData.title}</Typography>;
    $[6] = coursesData.title;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <th><IconTextWrapper>{t5}{t6}</IconTextWrapper></th>;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== DiplomaIcon) {
    t8 = <DiplomaIcon />;
    $[11] = DiplomaIcon;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== diplomaData.title) {
    t9 = <Typography variant="h6" component="h2">{diplomaData.title}</Typography>;
    $[13] = diplomaData.title;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <th><IconTextWrapper>{t8}{t9}</IconTextWrapper></th>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7) {
    t11 = <tr>{t4}{t7}{t10}</tr>;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <th />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      mb: 1
    };
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== coursesData.desc) {
    t14 = <th><Typography variant="bodySmall" paragraph={true} sx={t13}>{coursesData.desc}</Typography></th>;
    $[23] = coursesData.desc;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      mb: 1
    };
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== diplomaData.desc) {
    t16 = <th><Typography variant="bodySmall" paragraph={true} sx={t15}>{diplomaData.desc}</Typography></th>;
    $[26] = diplomaData.desc;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t14 || $[29] !== t16) {
    t17 = <tr>{t12}{t14}{t16}</tr>;
    $[28] = t14;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <th />;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== coursesData.ctaDataEvent || $[33] !== coursesData.ctaHref || $[34] !== coursesData.ctaLabel) {
    t19 = <th><Button variant="secondary" data-event={coursesData.ctaDataEvent} href={coursesData.ctaHref} component={Link}>{coursesData.ctaLabel}</Button></th>;
    $[32] = coursesData.ctaDataEvent;
    $[33] = coursesData.ctaHref;
    $[34] = coursesData.ctaLabel;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== diplomaData.ctaDataEvent || $[37] !== diplomaData.ctaHref || $[38] !== diplomaData.ctaLabel) {
    t20 = <th><Button variant="secondary" data-event={diplomaData.ctaDataEvent} component={Link} href={diplomaData.ctaHref}>{diplomaData.ctaLabel}</Button></th>;
    $[36] = diplomaData.ctaDataEvent;
    $[37] = diplomaData.ctaHref;
    $[38] = diplomaData.ctaLabel;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <tr>{t18}{t19}{t20}</tr>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t11 || $[44] !== t17 || $[45] !== t21) {
    t22 = <Thead>{t3}{t11}{t17}{t21}</Thead>;
    $[43] = t11;
    $[44] = t17;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== coursesData || $[48] !== diplomaData || $[49] !== topics) {
    let t24;
    if ($[51] !== coursesData || $[52] !== diplomaData) {
      t24 = topic => <Line key={topic.name} icon={topic.icon} label={topic.translation} coursesData={coursesData[topic.name]} diplomaData={diplomaData[topic.name]} />;
      $[51] = coursesData;
      $[52] = diplomaData;
      $[53] = t24;
    } else {
      t24 = $[53];
    }
    t23 = topics.map(t24);
    $[47] = coursesData;
    $[48] = diplomaData;
    $[49] = topics;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[54] !== t23) {
    t24 = <tbody>{t23}</tbody>;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t22 || $[57] !== t24) {
    t25 = <Table>{t22}{t24}</Table>;
    $[56] = t22;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}